import Sugar from 'sugar';
import { LitElement, css, html } from 'lit';
import { customElement, state, query, queryAll } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import { when } from 'lit/directives/when.js';
import { choose } from 'lit/directives/choose.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import {
  publicLedgerServiceContext,
  nsDirectoryServiceContext,
  ltnAuthServiceContext,
} from '../ns-services/contexts.js';
import type { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { NSDirectoryService } from '../ns-services/ns-directory-service.js';
import { DEVICE_SIGNED_OUT_EVENT, HederaCredentials } from '../ns-services/ns-public-ledger-service.js';
import { PublicLedgerService } from '../ns-services/ns-public-ledger-service.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';
import { createLogger } from '../ns-services/logger.js';
import { LiveAnnouncer } from '../helpers/live-announcer.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';
import { lambdaErrorCode } from '../helpers/lambda.js';
import {
  phraseCheckRefusal,
  requestPhraseChallenge,
  verifyPhraseAnswers,
  verifyWithPhrase,
} from '../helpers/phrase-check-api.js';
import './ns-loading.js';

const logger = createLogger('ns-mnemonic-verification');

enum ReminderState {
  ACTIVE,
  RECOMMENDED,
  REQUIRED,
  RESTRICTED,
}

enum VerificationCardView {
  INTRODUCTION,
  VERIFICATION,
  VERIFIED,
}

@customElement('ns-mnemonic-verification')
export class NSMnemonicVerification extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  @consume({ context: publicLedgerServiceContext })
  private __pLedgerService?: PublicLedgerService;

  @state()
  private __needsVerification: boolean = false;

  @state()
  private __reminderState: ReminderState = ReminderState.ACTIVE;

  @state()
  private __cardOpen: boolean = false;

  @state()
  private __verificationCardView: VerificationCardView = VerificationCardView.INTRODUCTION;

  @state()
  private __verificationMsg: string[] = [];

  @state()
  private __countDownTime?: string;

  @state()
  private __creds?: HederaCredentials | null = null;

  private readonly __subscriptions = new ButtressSubscriptions(this, () => this.__db);

  // Set once the profile has a ledger account to verify; until then, a change to the profile checks again
  private __ledgerLoaded = false;

  @state()
  private __loadingMessage: string = '';

  @state()
  private __statusAnnouncement: string = '';

  @state()
  private __mnemonicWords: string[] = Array.from({ length: 24 });

  private __mnemonicAnnouncer = new LiveAnnouncer(this);

  @query('#loader')
  private __loader?: HTMLElement;

  @query('#card-view')
  private __cardView?: HTMLElement;

  @query('#overlay-verification-card')
  private __overlay?: HTMLElement;

  @query('#verification-popup-message')
  private __popupMsg?: HTMLElement;

  @query('#mnemonic-form')
  private __form?: HTMLFormElement;

  @query('#error-message')
  private __errMsgElem?: HTMLElement;

  @queryAll('.word-input')
  private __wordInputs?: NodeListOf<HTMLInputElement>;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  // The server's check in progress (public-ledger/phrase-challenge): the browser never sees the letters, only where they are
  private __challengeId: string | null = null;

  private static readonly KEYLESS_CARD_MESSAGE =
    'To use this device with your nodeStream account, you need to enter your full 24-word recovery phrase';

  private static readonly KEYLESS_IMPORTANT_MESSAGE = 'You need to login to your public ledger account';

  private __verificationCardMessage: string = NSMnemonicVerification.KEYLESS_CARD_MESSAGE;

  private __verificationImportantMsg: string = NSMnemonicVerification.KEYLESS_IMPORTANT_MESSAGE;

  private __verificationPeriod: number = 90;

  private __countDownInterval?: NodeJS.Timeout;

  private __intervalMs: number = 1000;

  private __showIconDescription = () => {
    if (this.__popupMsg && !this.__popupMsg?.classList.contains('closed')) return;
    this.classList.add('showing-description');
  };
  private __hideIconDescription = () => this.classList.remove('showing-description');

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      :host {
        z-index: 901;

        position: relative;
      }

      :host(.showing-description) .description {
        display: block;

        z-index: 2;
      }

      :host(.showing-description) #verification-popup-message {
        display: none;
      }

      h2 {
        margin: 0 0 var(--wa-space-m);
        font-weight: 300;
      }

      .description {
        display: none;

        position: absolute;
        right: 0;

        background: var(--wa-color-brand-fill-loud);
        color: var(--wa-color-brand-on-loud);

        width: 200px;

        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);
      }

      #loader.hide {
        display: none;
      }
      #card-view.hide {
        display: none;
      }

      .mnemonic-key {
        width: 50px;
        height: 50px;

        z-index: 3;

        border-radius: var(--wa-border-radius-m);

        flex-shrink: 0;
      }

      .mnemonic-key.active {
        background: var(--wa-color-success-fill-loud);
      }

      .mnemonic-key.recommended {
        background: var(--ns-theme-amber);
      }

      .mnemonic-key.required,
      .mnemonic-key.restricted {
        background: var(--ns-theme-red);
      }

      ns-icon {
        width: 23px;
        height: 31px;
      }

      #verification-popup-message {
        position: absolute;
        top: 110%;
        right: 0;

        text-align: center;

        opacity: 1;

        display: flex;

        color: var(--ns-theme-black);

        font-size: var(--wa-font-size-xs);

        padding: var(--wa-space-xs);

        border-radius: var(--wa-border-radius-l);

        width: 200px;

        transition: all 0.3s;
        transition-timing-function: ease-in-out;
      }

      #verification-popup-message .close-btn::before {
        content: 'X';
        position: absolute;
        top: 5px;
        left: 5px;
        width: 15px;
        height: 15px;
        color: var(--ns-theme-black);
      }

      #verification-popup-message.hide {
        display: none;
      }

      #verification-popup-message.recommended {
        background: var(--ns-theme-amber);
      }

      #verification-popup-message.required {
        background: var(--ns-theme-red);
        color: var(--ns-theme-black);
      }

      #overlay-verification-card {
        position: fixed;
        right: 100%;
        bottom: 0;

        background: color-mix(in srgb, var(--ns-theme-black) 70%, transparent);

        opacity: 0;

        text-align: left;

        transition: opacity 0.3s;
        transition-timing-function: ease-in-out;
      }

      #verification-card {
        --ns-error-text-color: var(--ns-theme-red);

        position: relative;
        background: var(--ns-color-private-surface);
        color: var(--ns-theme-white);

        padding: var(--wa-space-xl);
        border-radius: var(--wa-border-radius-l);
        border: 1px solid var(--ns-theme-red);

        width: 50%;
        min-height: 500px;
      }

      #verification-card .cross-mark {
        position: absolute;
        top: 20px;
        right: 20px;
        color: var(--ns-theme-white);

        font-size: var(--wa-font-size-xl);
      }

      #overlay-verification-card.show {
        right: 0;

        opacity: 1;

        transition: opacity 0.3s;
        transition-timing-function: ease-in-out;
      }

      #verification-card .title {
        font-size: var(--wa-font-size-2xl);
        font-weight: 700;
      }

      #verification-card input[type='text'] {
        background-color: var(--ns-theme-black);
        color: var(--ns-theme-white);
        caret-color: var(--ns-theme-white);
        width: 100px; /* Fixed width for each box */
        height: 45px; /* Fixed height */
        font-size: var(--wa-font-size-m); /* Larger font size for digits */
        text-align: center;
        border: 2px solid var(--ns-theme-grey-mid);
        border-radius: var(--wa-border-radius-m);
        outline: none;
        transition:
          border-color 0.3s ease-in-out,
          box-shadow 0.3s ease-in-out;
        /* Remove default spin buttons for number type, if you were to use it */
        -moz-appearance: textfield;
        appearance: textfield;
      }

      #verification-card input[type='text']:focus {
        border-color: var(--ns-theme-purple);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--ns-theme-purple) 25%, transparent);
      }

      #error-message {
        font-size: var(--wa-font-size-m);
        font-weight: 700;
      }

      #error-message.hide {
        display: none;
      }

      .important-message .important {
        color: var(--ns-theme-red);
      }

      .mnemonic-grid-container {
        display: grid;
        grid-template-columns: repeat(4, 1fr);

        gap: var(--wa-space-m);

        justify-items: stretch;
        align-items: start;
      }

      .mnemonic-grid-container.one-column {
        grid-template-columns: 1fr;
      }

      #verification-card .word-input.filled {
        border-color: var(--ns-theme-green);
      }

      .clear-mnemonic {
        flex-shrink: 0;
      }

      .verified-title:focus-visible {
        outline: none;
      }

      @media (max-width: 1400px) {
        #verification-card {
          width: 65%;
        }
      }

      @media (max-width: 1200px) {
        #verification-card {
          width: 75%;
        }
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

    if (!this.__dir) throw new Error('Directory service not found');
    await this.__dir.initialised;

    // A ledger account made after the page loaded (the Create public ledger account job) arrives as a profile change
    this.__subscriptions.subscribe('people.*', () => this.__onProfileChanged());
    // This device signed out of the account while open: the key is gone, so the restricted recovery block opens
    this.__pLedgerService.addEventListener(DEVICE_SIGNED_OUT_EVENT, this.__onDeviceSignedOut);
    await this.__loadVerification();
  }

  // Starts again as a device with no key: any open check (its letters were for the forgotten key) closes, and the
  // restricted block asking for the whole recovery phrase opens straight away when the profile has a public ledger account
  private readonly __onDeviceSignedOut = async () => {
    window.clearInterval(this.__countDownInterval);
    this.__challengeId = null;
    this.__creds = null;
    this.__needsVerification = false;
    this.__countDownTime = undefined;
    this.__reminderState = ReminderState.ACTIVE;
    this.__verificationCardMessage = NSMnemonicVerification.KEYLESS_CARD_MESSAGE;
    this.__verificationImportantMsg = NSMnemonicVerification.KEYLESS_IMPORTANT_MESSAGE;
    this.__mnemonicWords = Array.from({ length: 24 });
    this.__closeCard();
    // Rendered as reset first, so updated() doesn't toggle the block that __loadVerification opens
    await this.updateComplete;
    this.__ledgerLoaded = false;
    await this.__loadVerification();
  };

  // The directory refreshes the profile on the same change, so this waits briefly for the ledger account to land on it.
  // An account made on this device is saved to it a moment after the profile, so that is waited for too; without it
  // the device would look unverified
  private async __onProfileChanged() {
    for (let attempt = 0; !this.__ledgerLoaded && attempt < 40; attempt += 1) {
      // verifiedAt is set by the first check, just after the letters are saved; without it the phrase would look overdue
      const ledgerCred = this.__dir?.me?.ledgerCred;
      const onProfile = !!ledgerCred?.accountId && !!ledgerCred?.verifiedAt;
      if (onProfile && ((await this.__pLedgerService?.getCreds()) || attempt >= 39)) {
        await this.__loadVerification();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }

  // A device with no key for a profile that has a public ledger account (one never added here, or signed out) gets the
  // restricted block asking for the whole recovery phrase straight away (DECISION 3.5), checked or not. A device holding
  // the key is reminded once the phrase has been checked: verifiedAt is set when the Recover public ledger keys card saves
  // the letters, and until then there is nothing to remind about
  private async __loadVerification() {
    if (this.__ledgerLoaded || !this.__dir || !this.__pLedgerService) return;
    const ledgerCred = this.__dir.me?.ledgerCred;
    if (!ledgerCred || !ledgerCred.accountId) {
      logger.warn('Missing profile and public ledger info');
      return;
    }
    this.__ledgerLoaded = true;

    this.__creds = await this.__pLedgerService.getCreds();
    if (!this.__creds) {
      this.__needsVerification = false;
      this.__reminderState = ReminderState.RESTRICTED;

      // await until the overly is added to the DOM tree
      await this.updateComplete;

      this.__verify();
      return;
    }
    if (!ledgerCred.verifiedAt) {
      // Loaded again once the check sets it (people.*)
      this.__ledgerLoaded = false;
      return;
    }

    if (this.__creds && this.__creds.accountId && this.__creds.publicKey && this.__creds.privateKeyEntry) {
      this.__verificationCardMessage = 'You need to verify your recovery phrase to continue working on this device';
      this.__verificationImportantMsg =
        'If you do not verify this device then you will not have the ability to complete any job card that requires public ledger signing.';
    }

    const verifiedAt = Sugar.Date.clone(Sugar.Date.create(ledgerCred.verifiedAt));
    const diffMs = Sugar.Date.create('now').getTime() - verifiedAt.getTime();
    const oneDayMs = 1000 * 60 * 60 * 24;
    const diffInDays = Math.floor(diffMs / oneDayMs);

    if (diffInDays >= this.__verificationPeriod) {
      this.__needsVerification = true;
      this.__reminderState = ReminderState.RECOMMENDED;
      this.__statusAnnouncement = 'Mnemonic verification is recommended.';

      const nintyDays = Sugar.Date.addDays(Sugar.Date.clone(verifiedAt), this.__verificationPeriod);
      const recommendedDays = Sugar.Date.addDays(Sugar.Date.clone(nintyDays), 2);
      const requiredDays = Sugar.Date.addDays(Sugar.Date.clone(nintyDays), 4);

      this.__countDownTime = this.__getCountdownInHours(recommendedDays, requiredDays);
    }
  }

  disconnectedCallback() {
    window.clearInterval(this.__countDownInterval);
    this.__pLedgerService?.removeEventListener(DEVICE_SIGNED_OUT_EVENT, this.__onDeviceSignedOut);
    super.disconnectedCallback();
  }

  updated(changedProps: any) {
    if (changedProps.has('__needsVerification') && this.__reminderState === ReminderState.RESTRICTED) {
      this.__verify();
    }
    if (changedProps.has('__verificationCardView') && this.__verificationCardView === VerificationCardView.VERIFIED) {
      this.updateComplete.then(() => this.__overlay?.querySelector<HTMLElement>('.verified-title')?.focus());
    }
  }

  private __toggleCardLoading(message: string = '') {
    if (!this.__loader) throw new Error('Missing loader element');
    if (!this.__cardView) throw new Error('Missing card view element');

    this.__loadingMessage = message;
    if (this.__loader.classList.contains('hide') && !this.__cardView.classList.contains('hide')) {
      this.__loader.classList.remove('hide');
      this.__cardView.classList.add('hide');
    } else {
      this.__loader.classList.add('hide');
      this.__cardView.classList.remove('hide');
    }
  }

  private readonly __dismissOnEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (ReminderState[this.__reminderState].toLowerCase() === 'restricted') return;
    this.__closeCard();
  };

  private __handleOverlayClick = (event: MouseEvent) => {
    if (event.target !== this.__overlay) return;
    if (ReminderState[this.__reminderState].toLowerCase() === 'restricted') return;
    this.__closeCard();
  };

  private __getCountdownInHours(recommendedDays: Date, end: Date): string | undefined {
    const start = Sugar.Date.create('now');
    if (start.getTime() > recommendedDays.getTime() && this.__reminderState !== ReminderState.REQUIRED) {
      this.__reminderState = ReminderState.REQUIRED;
      this.__statusAnnouncement = 'Mnemonic verification is required. Access will be restricted soon.';

      this.__countDownInterval = setInterval(() => {
        this.__countDownTime = this.__getCountdownInHours(recommendedDays, end);
      }, this.__intervalMs);
    }

    if (start.getTime() > end.getTime()) {
      window.clearInterval(this.__countDownInterval);
      this.__reminderState = ReminderState.RESTRICTED;
      if (!this.__overlay?.classList.contains('show')) {
        this.__verify();
      }
      return;
    }

    const diff = end.getTime() - start.getTime();

    const totalSeconds = Math.floor(diff / 1000);

    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    const formattedHours = String(totalHours).padStart(2, '0');
    const formattedMinutes = String(remainingMinutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  private __getSuffix(index: number) {
    return index % 10 === 1 && index % 100 !== 11
      ? 'st'
      : index % 10 === 2 && index % 100 !== 12
        ? 'nd'
        : index % 10 === 3 && index % 100 !== 13
          ? 'rd'
          : 'th';
  }

  private __hidePopUpMsg() {
    if (!this.__popupMsg) throw new Error('Missing the pop up message element');
    this.__popupMsg.classList.add('hide');
    this.__popupMsg.classList.add('closed');
    window.clearInterval(this.__countDownInterval);
  }

  private __getInertTargets(): HTMLElement[] {
    const appRoot = document.querySelector('ns-app')?.shadowRoot;
    const topBarRoot = appRoot?.querySelector('ns-top-bar')?.shadowRoot;

    return [
      appRoot?.querySelector<HTMLElement>('#mainMenu'),
      appRoot?.querySelector<HTMLElement>('#view-container'),
      topBarRoot?.querySelector<HTMLElement>('ns-company-switcher'),
      this.shadowRoot?.querySelector<HTMLElement>('#mnemonic-icon'),
    ].filter((el): el is HTMLElement => el !== null);
  }

  private __verify() {
    if (!this.__overlay) {
      logger.warn('Missing the verification card element');
      return;
    }

    if (
      this.__popupMsg &&
      this.__popupMsg.classList.contains('hide') &&
      !this.__popupMsg.classList.contains('closed')
    ) {
      this.__popupMsg?.classList.remove('hide');
    }

    if (this.__overlay.classList.contains('show')) {
      this.__overlay.classList.remove('show');
      this.__verificationCardView = VerificationCardView.INTRODUCTION;
      this.__cardOpen = false;
      return;
    }

    this.__overlay.classList.add('show');
    this.__popupMsg?.classList.add('hide');
    this.__cardOpen = true;
    this.__overlay.addEventListener('click', (ev) => this.__handleOverlayClick(ev));
    this.__getInertTargets().forEach((el) => el.toggleAttribute('inert', true));
    document.addEventListener('keydown', this.__dismissOnEscape);
    this.updateComplete.then(() => this.__overlay?.querySelector<HTMLElement>('.cross-mark')?.focus());
  }

  // Opens the letters (or, with no key on this device, the whole phrase). The letters are asked for by the server
  private __startVerification() {
    this.__verificationCardView = VerificationCardView.VERIFICATION;
    if (this.__creds && this.__creds.privateKeyEntry) this.__startChallenge();
  }

  private async __startChallenge(notice: string = '') {
    const me = this.__dir?.me;
    if (!me || !me.id) return;
    this.__challengeId = null;
    this.__verificationMsg = [];
    this.__toggleCardLoading('Preparing the check');
    try {
      const challenge = await requestPhraseChallenge(this.__auth?.token, me.id);
      this.__challengeId = challenge.challengeId;
      this.__verificationMsg = challenge.positions.map(
        ({ wordIndex, letterIndex }, idx) =>
          `${idx + 1}. ${letterIndex}${this.__getSuffix(letterIndex)} Character, ${wordIndex}${this.__getSuffix(wordIndex)} word`,
      );
      this.__toggleCardLoading();
      if (notice) await this.__showError(notice);
    } catch (err: unknown) {
      this.__toggleCardLoading();
      await this.__showError(
        phraseCheckRefusal(err, 'The recovery phrase check could not be started. Please try again.'),
      );
    }
  }

  private async __showError(message: string) {
    await this.updateComplete;
    if (!this.__errMsgElem) return;
    this.__errMsgElem.textContent = message;
    this.__errMsgElem.classList.remove('hide');
  }

  private async __submit() {
    if (!this.__form) throw new Error('Missing the form element');
    if (!this.__errMsgElem) throw new Error('Missing error message element');
    this.__errMsgElem.classList.add('hide');

    const answers = [1, 2, 3, 4].map(
      (n) => (this.__form?.querySelector(`#char${n}`) as HTMLInputElement | null)?.value ?? '',
    );
    if (answers.some((value) => !value)) {
      await this.__showError('Please enter the required letters.');
      return;
    }
    if (!this.__challengeId) {
      await this.__startChallenge('The check needed restarting. Please enter the letters asked for now.');
      return;
    }

    this.__toggleCardLoading('Checking your letters');
    try {
      const result = await verifyPhraseAnswers(this.__auth?.token, this.__challengeId, answers);
      this.__toggleCardLoading();
      if (result.verified) {
        this.__challengeId = null;
        this.__verificationCardView = VerificationCardView.VERIFIED;
        return;
      }
      if (result.attemptsLeft > 0) {
        await this.__showError(
          `Some of your letters are incorrect. ${result.attemptsLeft} ${result.attemptsLeft === 1 ? 'try' : 'tries'} left.`,
        );
        return;
      }
      await this.__startChallenge('Too many incorrect letters. Please enter the new letters asked for.');
    } catch (err: unknown) {
      this.__toggleCardLoading();
      const code = lambdaErrorCode(err);
      if (code === 'PHRASE_CHALLENGE_EXPIRED' || code === 'PHRASE_CHALLENGE_SPENT') {
        await this.__startChallenge('That check had ended. Please enter the new letters asked for.');
        return;
      }
      await this.__showError(phraseCheckRefusal(err, 'Your letters could not be checked.'));
    }
  }

  private async __submitMnemonic() {
    if (!this.__dir || !this.__dir.me || !this.__dir.me.ledgerCred) throw new Error('Missing dir profile');
    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');
    if (!this.__errMsgElem) throw new Error('Missing error message element');
    this.__errMsgElem.classList.add('hide');
    this.__toggleCardLoading('Recovering Account');

    try {
      if (!this.__mnemonicWords.every((word) => word && word.trim())) {
        throw new Error('Please enter the required letters.');
      }

      const phrase = this.__mnemonicWords.join(' ');
      await this.__pLedgerService.recoverAccount(phrase);

      // The phrase is in hand, so it answers the server's check, which records it as checked (ledgerCred.verifiedAt). A
      // profile whose letters were never saved (its Recover public ledger keys card not done) has nothing to check yet
      try {
        const result = await verifyWithPhrase(this.__auth?.token, this.__dir.me.id, phrase);
        if (!result.verified)
          throw new Error("Your recovery phrase couldn't be recorded as checked. Please try again.");
      } catch (err) {
        if (lambdaErrorCode(err) !== 'NO_RECOVERY_LETTERS') throw err;
      }
      this.__verificationCardView = VerificationCardView.VERIFIED;
      this.__reminderState = ReminderState.ACTIVE;

      this.__toggleCardLoading();
    } catch (err: unknown) {
      this.__toggleCardLoading();

      const message = phraseCheckRefusal(err, 'Please enter the required letters.');
      this.__errMsgElem.innerHTML = message as string;
      this.__errMsgElem.classList.remove('hide');
    }
  }

  private __updateMnemonicWord(idx: number, value: string) {
    this.__mnemonicWords[idx] = value.trim();
    this.requestUpdate();

    this.__errMsgElem?.classList.add('hide');
  }

  private __handlePhrasePaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const words = pasted.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return;

    event.preventDefault();

    const filled = words.slice(0, 24);
    this.__mnemonicWords = [...filled, ...Array.from({ length: 24 - filled.length })] as string[];
    this.__mnemonicAnnouncer.announce(`Recovery phrase pasted — ${filled.length} of 24 words filled in.`);

    this.updateComplete.then(() => {
      this.__wordInputs?.[Math.min(filled.length, 24) - 1]?.focus();
    });
  }

  private __clearMnemonic() {
    this.__mnemonicWords = Array.from({ length: 24 });
    this.__mnemonicAnnouncer.announce('All 24 words cleared.');
    this.__errMsgElem?.classList.add('hide');

    this.updateComplete.then(() => {
      this.__wordInputs?.[0]?.focus();
    });
  }

  private __closeCard() {
    if (!this.__overlay) {
      logger.warn('Missing the verification card element');
      return;
    }

    if (
      this.__popupMsg &&
      this.__popupMsg.classList.contains('hide') &&
      !this.__popupMsg.classList.contains('closed')
    ) {
      this.__popupMsg?.classList.remove('hide');
    }

    if (this.__overlay.classList.contains('show')) {
      this.__overlay.classList.remove('show');
      this.__verificationCardView = VerificationCardView.INTRODUCTION;
      this.__cardOpen = false;
      this.__getInertTargets().forEach((el) => el.toggleAttribute('inert', false));
      document.removeEventListener('keydown', this.__dismissOnEscape);
    }
  }

  renderMnemonicIcon(text: string = '') {
    const state = ReminderState[this.__reminderState].toLowerCase();
    const canClick = state === 'active' || state === 'restricted' ? false : true;
    return html`
      <div id="mnemonic-icon" class="layout horizontal center-center">
        ${when(
          text,
          () => html`
            <div id="verification-popup-message" class="${state}">
              <div class="text">
                ${text}
                ${when(
                  state === 'required',
                  () => html`
                    <strong>${this.__countDownTime}</strong>
                  `,
                )}
              </div>
              <button
                type="button"
                class="close-btn clickable"
                aria-label="Close"
                @click="${() => this.__hidePopUpMsg()}"
              ></button>
            </div>
          `,
        )}
        ${when(
          canClick,
          () => html`
            <button
              type="button"
              class="layout vertical center-center ${state} mnemonic-key clickable"
              @click="${this.__verify}"
              aria-label="${state === 'required'
                ? 'Mnemonic verification required, open recovery phrase check'
                : 'Verify recovery phrase'}"
              @mouseenter="${this.__showIconDescription}"
              @mouseleave="${this.__hideIconDescription}"
              @focusin="${this.__showIconDescription}"
              @focusout="${this.__hideIconDescription}"
            >
              <ns-icon icon="mnemonic-key"></ns-icon>
            </button>
          `,
          () => html`
            <div
              class="layout vertical center-center ${state} mnemonic-key"
              tabindex="0"
              role="img"
              aria-label="Mnemonic verification status: ${state}"
              aria-describedby="mnemonic-description"
              @mouseenter="${this.__showIconDescription}"
              @mouseleave="${this.__hideIconDescription}"
              @focusin="${this.__showIconDescription}"
              @focusout="${this.__hideIconDescription}"
            >
              <ns-icon icon="mnemonic-key"></ns-icon>
            </div>
          `,
        )}
      </div>
    `;
  }

  renderVerificationCard(message: string = '') {
    if (!this.__dir || !this.__dir.me || !this.__dir.me.ledgerCred || !this.__dir.me.ledgerCred.accountId) return;

    const state = ReminderState[this.__reminderState].toLowerCase();
    const canClick = state === 'restricted' ? true : false;

    return html`
      ${this.renderMnemonicIcon(message)}
      <div id="overlay-verification-card" class="layout vertical center-center h-100 w-100">
        <div
          id="verification-card"
          role="dialog"
          aria-modal="true"
          aria-label="Recovery phrase verification"
          ?inert="${!this.__cardOpen}"
        >
          <button
            type="button"
            ?hidden=${canClick}
            class="cross-mark clickable"
            aria-label="Close"
            @click=${() => this.__closeCard()}
          >
            &cross;
          </button>
          <ns-loading id="loader" class="h-100 hide" label=${this.__loadingMessage}></ns-loading>
          <div id="card-view" class="wrapper h-100">
            <div class="layout vertical h-100">
              <div
                class="layout vertical center-center ${ReminderState[
                  this.__reminderState
                ].toLowerCase()} mnemonic-key mb-3"
              >
                <ns-icon icon="mnemonic-key"></ns-icon>
              </div>
              ${choose(this.__verificationCardView, [
                [
                  VerificationCardView.INTRODUCTION,
                  () => html`
                    <h2 class="title mb-3">${this.__verificationCardMessage}</h2>
                    <div class="important-message mb-3 flex">
                      <div class="important">IMPORTANT!</div>
                      <div>${this.__verificationImportantMsg}</div>
                    </div>
                    <div>
                      <wa-button variant="brand" class="black-bg mb-3" @click="${() => this.__startVerification()}">
                        Next
                      </wa-button>
                    </div>
                  `,
                ],
                [VerificationCardView.VERIFICATION, () => this.renderVerificationStage()],
                [
                  VerificationCardView.VERIFIED,
                  () => html`
                    <h2 class="title mb-3 flex verified-title" tabindex="-1">
                      Thank you! Make sure you keep your recovery phrase in a secure non-digital location.
                    </h2>
                    <div>
                      <wa-button
                        variant="neutral"
                        class="raised mb-3"
                        @click="${async () => {
                          this.__needsVerification = false;
                          this.__reminderState = ReminderState.ACTIVE;
                          this.__creds = await this.__pLedgerService?.getCreds();
                          this.__closeCard();
                        }}"
                      >
                        Complete
                      </wa-button>
                    </div>
                  `,
                ],
              ])}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderVerificationStage() {
    let message = 'Enter the characters from your recovery phrase.';
    let submitMnemonic = false;
    let inputClassName = '';
    let maxlength = '1';
    if (!this.__creds || !this.__creds.privateKeyEntry) {
      message =
        'Enter your recovery phrase. Paste your full 24-word phrase into the first word to fill in the rest automatically.';
      submitMnemonic = true;
      inputClassName = 'word';
      maxlength = '';
      this.__verificationMsg = Array.from({ length: 24 }, (_, i) => `${i + 1}.`);
    }

    const gridClassName = this.__verificationMsg.length === 4 ? 'one-column' : '';
    return html`
      <div class="layout horizontal center-center">
        <h2 class="title mb-3 flex">${message}</h2>
        ${when(
          submitMnemonic,
          () => html`
            <wa-button
              type="button"
              variant="danger"
              class="icon-button circle clear-mnemonic"
              @click=${() => this.__clearMnemonic()}
              ?disabled=${!this.__mnemonicWords.some((word) => word && word.trim())}
            >
              <svg viewBox="0 0 4.5 4.5" width="1em" height="1em" aria-hidden="true">
                <path
                  d="M0.18 0.18L4.18 4.18M4.18 0.18L2.18 2.18L0.18 4.18"
                  stroke="currentColor"
                  stroke-width="0.5"
                  fill="none"
                />
              </svg>
              <span class="visually-hidden">Clear all</span>
            </wa-button>
          `,
        )}
      </div>
      <div id="error-message" class="error-text hide mb-3" role="alert"></div>
      <form id="mnemonic-form" class="mnemonic-grid-container ${gridClassName} mb-3">
        ${repeat(
          this.__verificationMsg,
          (msg: string, idx: number) => html`
            <div class="layout horizontal center mnemonic-grid-item">
              <label class="flex" for="char${idx + 1}">${msg}</label>
              ${submitMnemonic
                ? html`
                    <input
                      type="text"
                      class="word word-input medium ${this.__mnemonicWords[idx] ? 'filled' : ''}"
                      id="char${idx + 1}"
                      data-index="${idx}"
                      autocomplete="off"
                      autocapitalize="off"
                      autocorrect="off"
                      spellcheck="false"
                      .value=${this.__mnemonicWords[idx] ?? ''}
                      @input=${(e: Event) => this.__updateMnemonicWord(idx, (e.target as HTMLInputElement).value)}
                      @paste=${idx === 0 ? (e: ClipboardEvent) => this.__handlePhrasePaste(e) : undefined}
                    />
                  `
                : html`
                    <input
                      type="text"
                      class="${inputClassName}"
                      id="char${idx + 1}"
                      data-index="${idx}"
                      maxlength="${maxlength}"
                    />
                  `}
              <div class="flex"></div>
            </div>
          `,
        )}
      </form>
      ${this.__mnemonicAnnouncer.render()}
      <div>
        <wa-button
          variant="brand"
          class="black-bg mb-3"
          @click="${submitMnemonic ? this.__submitMnemonic : this.__submit}"
        >
          Submit
        </wa-button>
      </div>
    `;
  }

  protected override render() {
    return html`
      <div class="visually-hidden" aria-live="polite">${this.__statusAnnouncement}</div>
      ${when(
        this.__needsVerification,
        () => html`
          ${choose(this.__reminderState, [
            [ReminderState.RECOMMENDED, () => this.renderVerificationCard('Mnemonic verification is recommended.')],
            [
              ReminderState.REQUIRED,
              () => this.renderVerificationCard('Mnemonic verification is required. Access will be restricted in'),
            ],
            [ReminderState.RESTRICTED, () => this.renderVerificationCard()],
          ])}
        `,
        () => {
          return when(
            this.__creds,
            () => this.renderMnemonicIcon(),
            () => this.renderVerificationCard(),
          );
        },
      )}
      <div class="description small" id="mnemonic-description">
        Recommended 6 weeks after verification; you have 48h before it becomes Required, and another 48h before access
        is Restricted.
      </div>
    `;
  }
}
